"use client";

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { Rune } from "@rosette/ts/src/business/Rune.js";
import { Optional } from "@rosette/ts/src/util/Optional.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";


export type Orientation = "landscape" | "portrait";
export const LANDSCAPE: Orientation = "landscape";
export const PORTRAIT: Orientation = "portrait";


export type DeviceInfo = {
    width: number;
    height: number;
    remToPx: number;
    orientation: Orientation;
    isMobile: boolean;
    isDesktop: boolean;
    isDefault: boolean;
};


export function readCurrentDeviceInfo(): DeviceInfo {
    if (typeof window === "undefined" || typeof document === "undefined")
        return DEFAULT_DEVICE_INFO;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientation = (width > height ? LANDSCAPE : PORTRAIT);
    const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
    // Media queries, not remToPx: html's font-size steps up on desktop,
    // which would move these away from CSS's md and xl breakpoints.
    const isMobile = window.matchMedia("(width <= 48rem)").matches;
    const isDesktop = window.matchMedia("(width >= 80rem)").matches;
    const isDefault = false;
    return { width, height, remToPx, orientation, isMobile, isDesktop, isDefault };
}


const DEFAULT_DEVICE_INFO: DeviceInfo = {
    width: 1920,
    height: 1080,
    remToPx: 16,
    orientation: LANDSCAPE,
    isMobile: false,
    isDesktop: false,
    isDefault: true,
};


// The context allows hooks to work straight away within the context (no useEffect delay).
// The holder allows hooks to work outside the context (with useEffect delay).
const DeviceInfoContext = createContext<DeviceInfo | null>(null);
const theDeviceInfoHolder = new Rune<Optional<DeviceInfo>>(Optional.empty());


export function useOptionalDeviceInfo(): DeviceInfo | null {
    const ctxDeviceInfo = useContext(DeviceInfoContext);
    const holderDeviceInfo = useRune(theDeviceInfoHolder).getOrNull();
    return ctxDeviceInfo ?? holderDeviceInfo;
}


export function useDeviceInfo(): DeviceInfo {
    const deviceInfo = useOptionalDeviceInfo();
    if (!deviceInfo)
        throw new Error("useDeviceInfo must be used within a DeviceInfoContext");

    return deviceInfo;
}


export function useOptionalIsMobile(): boolean | null {
    const deviceInfo = useOptionalDeviceInfo();
    return deviceInfo && deviceInfo.isMobile;
}


export function useIsMobile(): boolean {
    const deviceInfo = useDeviceInfo();
    return deviceInfo.isMobile;
}


export function useOptionalIsDesktop(): boolean | null {
    const deviceInfo = useOptionalDeviceInfo();
    return deviceInfo && deviceInfo.isDesktop;
}


export function useIsDesktop(): boolean {
    const deviceInfo = useDeviceInfo();
    return deviceInfo.isDesktop;
}


export function useOptionalOrientation(): Orientation | null {
    const deviceInfo = useOptionalDeviceInfo();
    return deviceInfo?.orientation ?? null;
}


export function useOrientation(): Orientation {
    const deviceInfo = useDeviceInfo();
    return deviceInfo.orientation;
}


interface DeviceInfoProviderProps {
    children: ReactNode;
}


export function DeviceInfoProvider({ children }: DeviceInfoProviderProps) {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(DEFAULT_DEVICE_INFO);

    useEffect(() => {
        const updateDeviceInfo = () => {
            const newDeviceInfo = readCurrentDeviceInfo();
            setDeviceInfo(newDeviceInfo);
            theDeviceInfoHolder.set(Optional.of(newDeviceInfo));
        };
        updateDeviceInfo();

        let timeoutID: number | NodeJS.Timeout | null = null;
        const updateOrientationWithTimeout = () => {
            updateDeviceInfo();

            if (timeoutID !== null) {
                clearTimeout(timeoutID);
            }

            timeoutID = setTimeout(() => {
                timeoutID = null;
                updateDeviceInfo();
            }, 100);
        };

        if (screen.orientation) {
            screen.orientation.addEventListener("change", updateOrientationWithTimeout);
        }
        window.addEventListener("resize", updateOrientationWithTimeout);

        return () => {
            if (timeoutID !== null) {
                clearTimeout(timeoutID);
            }
            if (screen.orientation) {
                screen.orientation.removeEventListener("change", updateOrientationWithTimeout);
            }
            window.removeEventListener("resize", updateOrientationWithTimeout);
        };
    }, []);

    return (
        <DeviceInfoContext.Provider value={deviceInfo}>
            {children}
        </DeviceInfoContext.Provider>
    );
}
